<template>
  <Dialog 
    :open="isOpen" 
    @update:open="handleUpdateOpen"
  >
    <DialogContent 
      class="sm:max-w-md [&>button]:hidden"
      @pointer-down-outside="(event) => event.preventDefault()"
      @escape-key-down="(event) => event.preventDefault()"
    >
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <WifiOff class="h-5 w-5 text-destructive" />
          {{ t("common.network.offlineTitle") }}
        </DialogTitle>
        <DialogDescription>
          {{ t("common.network.offlineDescription") }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button @click="handleRefresh" :disabled="isRefreshing">
          <RefreshCw v-if="isRefreshing" class="h-4 w-4 mr-2 animate-spin" />
          <RefreshCw v-else class="h-4 w-4 mr-2" />
          {{ isRefreshing ? t("common.network.refreshing") : t("common.network.refresh") }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { WifiOff, RefreshCw } from "lucide-vue-next"
import { useRestaurantStore } from "@/stores/restaurant"
import { webSocketService } from "@/services/websocket"
import { apiService } from "@/services/api"
import { toast } from "vue-sonner"
import { useI18n } from "@/composables/useI18n"

const { t } = useI18n()

interface Props {
  open: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const isOpen = ref(props.open)
const isRefreshing = ref(false)
const shouldAllowClose = ref(false)
const restaurantStore = useRestaurantStore()

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
    // If modal is being closed and connection is restored, allow closing
    // eslint-disable-next-line no-undef
    if (!newValue && typeof navigator !== "undefined" && navigator.onLine) {
      shouldAllowClose.value = true
    }
  }
)

watch(isOpen, (newValue) => {
  if (shouldAllowClose.value || !newValue) {
    emit("update:open", newValue)
  }
  // Reset flag after closing
  if (!newValue) {
    shouldAllowClose.value = false
  }
})

// Handle update:open event - prevent closing unless shouldAllowClose is true
const handleUpdateOpen = (value: boolean) => {
  // If trying to close, check if connection is restored
  if (!value) {
    // Allow closing if connection is restored or shouldAllowClose flag is set
    // eslint-disable-next-line no-undef
    if (typeof navigator !== "undefined" && navigator.onLine) {
      shouldAllowClose.value = true
      isOpen.value = false
      return
    }
    // Prevent closing by clicking outside or ESC - only allow programmatic closing
    if (!shouldAllowClose.value) {
      // Force dialog to stay open
      isOpen.value = true
      return
    }
  }
  isOpen.value = value
}

const handleRefresh = async () => {
  if (!restaurantStore.restaurant?.id) {
    toast.error(t("common.network.noRestaurant"), {
      description: t("common.network.noRestaurantDescription"),
    })
    return
  }

  isRefreshing.value = true

  try {
    // eslint-disable-next-line no-console
    console.log("🔄 [NetworkOfflineModal] Checking health endpoint...")

    // Check health endpoint to verify connection
    const healthResponse = await apiService.get<{ status: string }>("/health")
    
    if (healthResponse.status !== "ok") {
      throw new Error("Health check failed")
    }

    // eslint-disable-next-line no-console
    console.log("✅ [NetworkOfflineModal] Health check passed, connection restored")

    const packageType = restaurantStore.restaurant.packageType

    // For FULL package, also refresh orders
    if (packageType === "FULL" || !packageType) {
      // eslint-disable-next-line no-console
      console.log("🔄 [NetworkOfflineModal] Refreshing orders for FULL package")
      
      await Promise.all([
        restaurantStore.fetchActiveOrders(restaurantStore.restaurant.id),
        restaurantStore.fetchOrders(restaurantStore.restaurant.id, 1, 10),
      ])

      toast.success(t("common.network.dataRefreshed"), {
        description: t("common.network.ordersUpdated"),
      })
    } else {
      // eslint-disable-next-line no-console
      console.log("⏭️ [NetworkOfflineModal] Skipping orders refresh for SIMPLE package")
      
      toast.success(t("common.network.connectionRestored"), {
        description: t("common.network.connectionRestoredDescription"),
      })
    }

    // Check and reconnect websocket if needed
    if (!webSocketService.socket?.connected) {
      // eslint-disable-next-line no-console
      console.log("🔄 [NetworkOfflineModal] Reconnecting websocket...")
      webSocketService.disconnect()
      webSocketService.connect()
    }

    // Close modal
    shouldAllowClose.value = true
    isOpen.value = false
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("❌ [NetworkOfflineModal] Error checking health or refreshing:", error)
    toast.error(t("common.network.connectionError"), {
      description: t("common.network.connectionErrorDescription"),
    })
  } finally {
    isRefreshing.value = false
  }
}
</script>
